import { can } from '@misumi/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ExcelCopyIcon, IconUpload } from '../components/icons'
import { useAssignToProject } from '../components/ui/AssignToProjectProvider'
import { Button, buttonClassName } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Tabs } from '../components/ui/Tabs'
import { useToast } from '../components/ui/ToastProvider'
import { useAuth } from '../lib/auth/auth-context'
import { useCartCount } from '../lib/cart/cart-count-context'
import { CopyFromExcelModal } from '../verticals/cart/CopyFromExcelModal'
import type { CartCsvRow } from '../verticals/cart/cart-csv'
import { importCatalogRows } from '../verticals/cart/import-catalog-rows'
import { defaultSkuResolveService } from '../verticals/cart/sku-resolver'
import { addCatalogResultToCart } from '../verticals/catalog/catalog-add-to-cart'
import { useUploadFlowContext } from '../verticals/uploads/UploadFlowProvider'
import { CAD_ACCEPT_ATTR, Dropzone } from '../verticals/uploads/components/Dropzone'
import { HelpAndResources } from './HelpAndResources'
import { HomeCategoryCarousel } from './HomeCategoryCarousel'
import { OpenQuotes } from './OpenQuotes'
import { RecentOrders } from './RecentOrders'

/**
 * Home — primary landing/dashboard, sourcing entry point (screens.md §2,
 * UID-27's "shell frame" deliverable). Greeting header, upload/CAD entry
 * points, category row, and the activity tabs panel are all real layout.
 * The category row is now LIVE (UID-250): `<HomeCategoryCarousel>` renders
 * the real off-the-shelf category carousel from `GET /catalog/categories`,
 * replacing the `<EmptyState>` "Coming in WS-2" stub. The activity panel's
 * Orders tab is LIVE
 * (`<RecentOrders>`, UID-90); its Open-quotes tab remains a static stub
 * pending confirmed RFQ-list semantics.
 *
 * UID-123 (S8 design-fidelity round 2, item 8): the design shows a tab-aware
 * "View all orders"/"View all quotes" link next to the tabs
 * (`Fictiv Platform.dc.html:448`, `homeTabViewLabel`/`homeTabViewClick`).
 * `<RecentOrders>` (UID-90) already renders its OWN "View all orders →"
 * link inside the Orders panel — so only the missing "View all quotes" half
 * is added here, at the Open-quotes tab level, to avoid two competing
 * "View all orders" links when that tab is active. It routes to the real,
 * already-shipped `/rfq` list (`RfqListPage`, registry `basePath: '/rfq'`),
 * not an invented route.
 *
 * UID-125: the part-upload drop zone below was previously cosmetic only — a
 * bare `<input type="file">` with no `onChange`, no drag/drop, and a
 * `display:none` a11y regression. It now wires to the EXACT SAME end-to-end
 * flow already shipped for `/uploads` (`UploadsPage.tsx`): `<Dropzone>`
 * (the `.sr-only`-hidden, keyboard-focusable input + drag/drop handlers,
 * `accept` derived from the shared `ACCEPTED_CAD_EXTENSIONS` allowlist) and
 * `useUploadFlow` (extracted from `UploadsPage` into `useUploads.ts` so both
 * screens share one presign→PUT→create-session→navigate seam rather than a
 * second copy of it). No new backend endpoint, contract, or pipeline — same
 * real browser→S3 byte transfer (UID-124) as `/uploads`, not a presign-only
 * stub.
 *
 * Gated on the `bom:upload` capability (`@misumi/shared` `can`), the same
 * capability the vertical registry uses to hide the "Uploads" nav link for
 * a role that doesn't hold it (`verticals/registry.tsx`). Unlike the nav
 * link, the drop zone itself stays visible while signed out or for a role
 * without the capability — Home is the public-ish landing surface and the
 * cosmetic entry point was already visible in that state before this ticket
 * — but it renders `disabled` so a user who can't actually start an upload
 * (no session, or a non-Engineer role) can't trigger the flow from here
 * either, matching what they'd hit going through the gated `/uploads` nav
 * item.
 */
/**
 * UID-583 — this page no longer takes a `service` prop. The upload flow it
 * used to inject into now lives in `UploadFlowProvider` above `<Routes>`
 * (ADR-0173 D2), which owns that DI seam instead; nothing else on Home
 * consumed it. Tests inject through the provider.
 */
export function HomePage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()
  const { promptAssignToProject } = useAssignToProject()
  // ADR-0095 D6 (UID-302): design's `homeTab:'quotes'` — was defensible as
  // 'orders' while the quotes tab was a dead `<EmptyState>` stub (landing on
  // an empty stub is worse than landing on live orders); now that Open
  // quotes is live, the design's default applies.
  const [activityTab, setActivityTab] = useState('quotes')
  // RecentOrders requires an onCountChange callback, but Home no longer surfaces the count (the
  // tab badge was removed per the design), so this is a no-op — no state to track.
  const handleOrdersCountChange = useCallback(() => {}, [])
  // UID-583 (ADR-0173 D1/D2) — the flow is owned by `UploadFlowProvider`
  // above `<Routes>`, and it navigates to the staging page as soon as the
  // session exists. UID-550's on-page batch panel is gone with it: the wait
  // is now shown by `StagingPage`'s "Analyzing your files" panel, which is
  // where the user already is.
  const { handleFilesSelected, batchError, clearBatchError } = useUploadFlowContext()
  useEffect(() => clearBatchError(), [clearBatchError])
  const [showCopyFromExcel, setShowCopyFromExcel] = useState(false)

  // UID-250: drives the category carousel's Prev/Next arrows, which the
  // design puts in this heading row rather than inside the carousel itself
  // (`Fictiv Platform.dc.html` ~L461-481) — `null` while the carousel is
  // loading/error/empty, since there's nothing to scroll yet, so the
  // handlers below are a harmless no-op in those states.
  const categoryScrollRef = useRef<HTMLDivElement>(null)

  const greetingName =
    auth.status === 'authenticated' ? auth.session.displayName?.split(' ')[0] : undefined

  const canUpload =
    auth.status === 'authenticated' && can(auth.session.accountContext.role, 'bom:upload')

  /**
   * "Copy from Excel" (`Fictiv Platform.dc.html:451-458`'s `onOpenXl`
   * footer button): reuses the exact same `<CopyFromExcelModal>` paste UI
   * and `parseCartCsv` the Cart redesign ships, resolving + adding each row
   * through the shared `importCatalogRows` loop (`verticals/cart/
   * import-catalog-rows.ts`) — the same resolve/add/error-collection shape
   * `CartPage.tsx`'s `handleParsedBulkRows` uses, factored out so the two
   * don't drift. Here the per-row add is `addCatalogResultToCart`
   * (`verticals/catalog/catalog-add-to-cart.ts`) — the same `POST
   * /cart/lines` call (+ 409-merge fallback) `CartPage`'s own
   * `handleAddLine` and the catalog "Add" button already use, generalized to
   * accept the pasted row's quantity instead of a flat 1. No new endpoint;
   * part-number resolution reuses the same shared cross-source resolver
   * (`sku-resolver.ts` — MISUMI/partner catalog + Fictiv parts library) Cart's
   * bulk-import path now uses, replacing the old fixture-only lookup.
   *
   * On success, toasts a summary and navigates to `/cart` so the buyer can
   * review what landed — a judgment call (not specified by the mock, which
   * only shows the paste UI) matching the "review before you leave" pattern
   * the rest of Home's bulk-add entry points already follow. Code-review
   * cleanup: a partial success (some added, some failed) previously fired
   * TWO toasts back to back (a success summary, then the first error); now
   * folded into one combined message so only one toast shows.
   *
   * UID-423 (ADR-0127 §H) — also pushes the top-bar badge's COMBINED count
   * (part/BOM lines + requested services), matching every other add path
   * (`ResultCard`, `ConfiguratorCore`, `RecentlyViewed`,
   * `RecentlyViewedQuickViewModal`, `AgentSearchPage`). Home previously
   * pushed nothing at all, leaving the badge stale until the next cart
   * visit. `addCatalogResultToCart`'s own `AddToCartOutcome.lineCount` is
   * already the combined count (see that module), so the LAST successful
   * row's outcome is pushed once the whole batch settles — one push, not
   * one per row, matching `handleBulkAddToCart` (PartsLibraryPage)'s
   * "refresh the badge once at the end" posture.
   */
  async function handleHomeCopyFromExcel({
    rows,
    errors,
  }: {
    rows: readonly CartCsvRow[]
    errors: readonly string[]
  }) {
    let lastLineCount: number | null = null
    const { addedCount, errors: combinedErrors } = await importCatalogRows(
      { rows, errors },
      {
        resolve: (partNumber) => defaultSkuResolveService.resolve(partNumber),
        addLine: async (sourceable, row) => {
          const outcome = await addCatalogResultToCart(sourceable, row.quantity)
          lastLineCount = outcome.lineCount
          return outcome
        },
      },
    )
    if (addedCount > 0) {
      if (lastLineCount !== null) setLineCount(lastLineCount)
      showToast(
        combinedErrors.length > 0
          ? `Added ${addedCount} of ${rows.length} parts to your cart — ${combinedErrors[0]}`
          : `Added ${addedCount} ${addedCount === 1 ? 'part' : 'parts'} to your cart`,
        combinedErrors.length > 0 ? 'info' : 'success',
      )
      navigate('/cart')
    } else if (combinedErrors.length > 0) {
      showToast(combinedErrors[0] ?? 'Some rows could not be added.', 'error')
    }
  }

  /** How far a Prev/Next click scrolls the category carousel, in px — a bit
   * less than the design's 2 x 272px column width so the trailing card of
   * the previous page stays partially visible as a scroll-position cue. */
  function scrollCategories(direction: 1 | -1) {
    categoryScrollRef.current?.scrollBy({ left: direction * 520, behavior: 'smooth' })
  }

  return (
    <section className="home-page" aria-labelledby="home-heading">
      <div>
        <h1 id="home-heading" className="display-heading">
          {greetingName
            ? `What are you sourcing today, ${greetingName}?`
            : 'What are you sourcing today?'}
        </h1>
        <p className="home-greeting-subtitle">
          One place to source your whole bill of materials — standard, configurable, and custom.
        </p>
      </div>

      {/* UID-293: the mock (`Fictiv Platform.dc.html:451-458`) has no "Upload your parts"
      heading — the dropzone and the "Already have a bill of materials?" footer sit together
      in one card. `aria-label` carries the section's accessible name instead of a visible
      heading referencing a since-removed <h2>. */}
      <section aria-label="Upload your parts">
        <Card className="home-upload-card" style={{ padding: 0 }}>
          <Dropzone
            onFilesSelected={(files) =>
              void handleFilesSelected(files, undefined, promptAssignToProject)
            }
            disabled={!canUpload}
            bare
          />
          <div className="home-upload-footer">
            <div>
              <p className="home-upload-footer-title">Already have a bill of materials?</p>
              <p className="home-upload-footer-desc">
                Import a BOM spreadsheet and we'll match parts automatically.
              </p>
            </div>
            <div className="home-upload-footer-actions">
              <Button variant="outline" size="lg" onClick={() => setShowCopyFromExcel(true)}>
                <ExcelCopyIcon />
                Copy from Excel
              </Button>
              {/* Implicit label→input nesting only — no `htmlFor`. A label that both wraps AND
              points at the same input via `htmlFor` fires the file dialog twice and the browser
              cancels the duplicate, so the picker never opens (the UID-60 "Upload a CSV does
              nothing" bug `CsvImportControl.tsx` documents); nesting alone is one clean trigger. */}
              <label className={buttonClassName({ variant: 'primary', size: 'lg' })}>
                <input
                  type="file"
                  multiple
                  accept={CAD_ACCEPT_ATTR}
                  className="sr-only"
                  disabled={!canUpload}
                  onChange={(event) => {
                    if (event.target.files) {
                      void handleFilesSelected(
                        [...event.target.files],
                        undefined,
                        promptAssignToProject,
                      )
                    }
                    event.target.value = ''
                  }}
                />
                <IconUpload />
                Choose files or BOM
              </label>
            </div>
          </div>
        </Card>
        {batchError && (
          <p role="alert" className="uploads-batch-error">
            {batchError}
          </p>
        )}
        <CopyFromExcelModal
          open={showCopyFromExcel}
          onClose={() => setShowCopyFromExcel(false)}
          onParsed={(result) => void handleHomeCopyFromExcel(result)}
        />
      </section>

      <section aria-labelledby="home-categories-heading">
        <div className="home-catalog-heading-row">
          <h2 id="home-categories-heading" className="home-section-title">
            Browse the catalog
          </h2>
          <Link to="/catalog" className="home-catalog-view-all-link">
            View full catalog <span aria-hidden="true">→</span>
          </Link>
          <div className="home-category-carousel-nav">
            <button
              type="button"
              className="home-category-carousel-prev"
              aria-label="Previous categories"
              onClick={() => scrollCategories(-1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="home-category-carousel-next"
              aria-label="Next categories"
              onClick={() => scrollCategories(1)}
            >
              ›
            </button>
          </div>
        </div>
        <HomeCategoryCarousel ref={categoryScrollRef} />
      </section>

      {/* UID-123 item 9: the design (`Fictiv Platform.dc.html:442-449`)
      renders the tabs directly under the two-column grid above with no
      section heading — the earlier "Recent activity" <h2> has no
      counterpart there. Removed, along with the now-dangling
      `aria-labelledby` that pointed at it; the Tabs component's own
      `aria-label="Recent activity"` carries the accessible name instead. */}
      <section className="home-activity-panel">
        {/*
         * The Orders tab is LIVE (UID-90): `<RecentOrders>` fetches
         * `GET /api/orders` (reusing UID-63's client/type/status-chip),
         * drives this tab's `count` badge from the returned list length, and
         * renders the most-recent 5 rows with a "View all → /orders" link.
         * It only fetches when the session is authenticated — `order:read`
         * is an account-baseline scope, but there's no account to scope it
         * to while signed out.
         *
         * The Open-quotes tab is now ALSO LIVE (ADR-0095, UID-302):
         * `<OpenQuotes>` fetches `GET /rfq`, filters to
         * requested/quoting/quoted, and renders the most-recent 6 rows —
         * mirroring `<RecentOrders>`'s state shape but taking NO
         * `onCountChange` prop (ADR-0095 D5: the tab badge was already
         * removed in the UID-212 follow-up). UID-123 item 8 adds the
         * tab-aware "View all quotes" link, shown only while that tab is
         * active (the Orders tab already has its own equivalent link inside
         * `<RecentOrders>` — see above).
         */}
        <div className="home-activity-tabs-row">
          <Tabs
            // UID-212 follow-up: the design's Home activity tabs are plain labels — no blue rounded
            // `.tab-count` badge (that shared pill stays for screens whose design DOES show it) —
            // and are ordered Open quotes first, Recent orders second.
            items={[
              { label: 'Open quotes', id: 'quotes' },
              { label: 'Recent orders', id: 'orders' },
            ]}
            activeId={activityTab}
            onChange={setActivityTab}
            aria-label="Recent activity"
          >
            {(activeId) =>
              activeId === 'orders' ? (
                <RecentOrders
                  authenticated={auth.status === 'authenticated'}
                  onCountChange={handleOrdersCountChange}
                />
              ) : (
                <OpenQuotes authenticated={auth.status === 'authenticated'} />
              )
            }
          </Tabs>
          {/* UID-212 follow-up: a single tab-aware "View all …" link in the UPPER-RIGHT of the tabs
          row (no trailing arrow, no underline) for BOTH tabs — replacing RecentOrders' own bottom
          "View all orders →" link, so orders and quotes are consistent. */}
          <Link
            to={activityTab === 'orders' ? '/orders' : '/rfq'}
            className="home-activity-view-all-link"
          >
            {activityTab === 'orders' ? 'View all orders' : 'View all quotes'}
          </Link>
        </div>
      </section>

      {/* UID-248/UID-293: the design's "Help and resources" card grid
      (`Fictiv Platform.dc.html:592`) sits AFTER the Open-quotes/Recent-orders
      activity tabs, not above them — moved here to match. Only "Material
      selection" is a real entry point (the Materials.AI chat modal) — see
      `HelpAndResources.tsx`'s own doc comment. */}
      <HelpAndResources />
    </section>
  )
}
